<script lang="ts">
    import Router from 'svelte-spa-router';
    import Header from './components/Header.svelte';
    import Sidebar from './components/Sidebar.svelte';
    import Dashboard from './pages/Dashboard.svelte';
    import Settings from './pages/Settings.svelte';
    import FormTest from './pages/FormTest.svelte';
    import Logs from './pages/Logs.svelte';
    import System from './pages/System.svelte';
    import Terminal from './pages/Terminal.svelte';
    import Modules from './pages/Modules.svelte';
    import NotFound from './pages/NotFound.svelte';
    import StatusTest from './pages/StatusTest.svelte';
    import ModalTest from './pages/ModalTest.svelte';
    import Toaster from './components/Toaster.svelte';
    import AuthModal from './components/AuthModal.svelte';
    import { onMount, onDestroy } from 'svelte';
    import { systemStatus } from './stores/status';
    import { auth } from './stores/auth';
    import { addToast } from './stores/toast';
    import { push } from 'svelte-spa-router';
    import { t } from './stores/locale';

    const STATUS_TOAST: Record<string, { type: 'info'|'success'|'warning'|'error'; title: string; message: string }> = {
        ok:      { type: 'success', title: 'System OK',      message: 'All systems are operating normally.' },
        warning: { type: 'warning', title: 'System Warning',  message: 'A warning condition has been detected.' },
        error:   { type: 'error',   title: 'System Error',    message: 'An error has been reported by the system.' },
    };

    let prevStatus = '';
    let authModalOpen = false;
    let blinkInterval: number | null = null;
    let blinkVisible = true;

    systemStatus.subscribe(status => {
        if (prevStatus && prevStatus !== status) {
            const cfg = STATUS_TOAST[status];
            if (cfg) addToast(cfg.message, cfg.type, { title: cfg.title, onClick: () => push('/') });
        }
        prevStatus = status;
    });

    const routes = {
        '/':         Dashboard,
        '/logs':     Logs,
        '/system':   System,
        '/settings': Settings,
        '/forms':    FormTest,
        '/modals':   ModalTest,
        '/status':   StatusTest,
        '/modules':  Modules,
        '/terminal': Terminal,
        '*':         NotFound,
    };

    const STATUS_COLOR: Record<string, string> = {
        ok:      '#4CAF50',
        warning: '#FFC107',
        error:   '#F44336',
    };

    const STATUS_TITLE_KEY: Record<string, string> = {
        ok:      'app.statusOk',
        warning: 'app.statusWarning',
        error:   'app.statusError',
    };

    function setFavicon(status: string, visible = true) {
        const color = visible ? STATUS_COLOR[status] : 'transparent';
        const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'>`
            + `<circle cx='16' cy='16' r='14' fill='${color}'/>`
            + `</svg>`;
        const href = `data:image/svg+xml,${encodeURIComponent(svg)}`;

        let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        link.href = href;
    }

    function startBlinking() {
        if (blinkInterval) return;
        blinkInterval = window.setInterval(() => {
            blinkVisible = !blinkVisible;
            setFavicon($systemStatus, blinkVisible);
        }, 500);
    }

    function stopBlinking() {
        if (blinkInterval) {
            clearInterval(blinkInterval);
            blinkInterval = null;
        }
        blinkVisible = true;
    }

    $: {
        document.title = $t(STATUS_TITLE_KEY[$systemStatus]);
        if ($systemStatus === 'error') {
            startBlinking();
        } else {
            stopBlinking();
            setFavicon($systemStatus);
        }
    }

    onDestroy(() => {
        stopBlinking();
    });
</script>

<div class="app-layout">
    <Sidebar loginCallback={() => { authModalOpen = true; }} />
    <div class="app-content">
        <Header />
        <main>
            <Router {routes} />
        </main>
    </div>
    <Toaster />
    <AuthModal bind:open={authModalOpen} />
</div>

<style lang="scss">
    .app-layout {
        display: flex;
        flex-direction: row; // Sidebar + Content side-by-side
        height: 100%;
        background-color: $color-sidebar-bg; // Sidebar background extends behind header
    }

    .app-content {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-width: 0; // Prevent flex overflow
        height: 100%;
        overflow: hidden;
        // Background here is transparent so it shows app-layout's sidebar-bg color
    }

    main {
        flex: 1;
        padding: 1.5em;
        overflow-y: auto;
        color: $color-text;
        background-color: $color-background; // Grey background for content
        border-top-left-radius: 24px; // Rounded corner starts BELOW the header
        // This creates the effect: Header is white (like sidebar), Content is grey and rounded.
    }
</style>